// Hook that calls the backend export endpoint and triggers an XLSX download.
import { useState, useCallback } from 'react';
import type { StartupFilters } from '../types/startup';
import { startupsService } from '../services/startups.service';
import { captureException } from '@startus/lib';

export interface UseStartupsCsvExportResult {
  exportCsv: () => Promise<void>;
  exporting: boolean;
  exportError: unknown; // pass to getErrorMessage(exportError, t) at display site
}

/**
 * Requests a server-generated XLSX export for the currently applied filters
 * and triggers a browser download without any client-side spreadsheet assembly.
 */
export function useStartupsCsvExport(
  filters: StartupFilters,
  search: string
): UseStartupsCsvExportResult {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<unknown>(null);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await startupsService.exportStartups({
        search: search || undefined,
        ...filters,
      });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = `startups-${new Date().toISOString().slice(0, 10)}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      captureException(err);
      console.error('[useStartupsCsvExport] Export failed:', err);
      setExportError(err);
    } finally {
      setExporting(false);
    }
  }, [filters, search]);

  return { exportCsv, exporting, exportError };
}
